import type { SuggestionStatus } from '@adweb/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { CheckCircle2, ChevronDown, Plus, Sparkles, ThumbsUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

import type { AdminSuggestion, ClientSuggestion } from './suggestion-meta';
import { SuggestionAdminModal, SuggestionStatusPill, targetLabel } from './SuggestionAdminModal';
import { SuggestionCard } from './SuggestionCard';
import { SuggestionDetailModal } from './SuggestionDetailModal';
import { SuggestionEditorModal } from './SuggestionEditorModal';

const GRID = 'grid gap-4 sm:grid-cols-2 xl:grid-cols-3';

function EmptyShowcase({ text, action }: { text: string; action?: React.ReactNode }) {
  return (
    <div className="surface flex flex-col items-center gap-3 px-6 py-12 text-center">
      <Sparkles className="h-8 w-8 text-accent" aria-hidden="true" />
      <p className="max-w-md text-sm text-slate-600">{text}</p>
      {action}
    </div>
  );
}

/** Vitrine client : suggestions proposées à l'organisation, masquées repliées en bas. */
function ClientSuggestions() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [showHidden, setShowHidden] = useState(false);
  const markedSeen = useRef(false);

  const list = useQuery({
    queryKey: ['suggestions', 'list'],
    queryFn: async () => (await apiClient.get<ClientSuggestion[]>('/suggestions')).data,
    // Les badges « Nouveau » de la visite en cours restent affichés jusqu'au départ.
    refetchOnWindowFocus: false,
  });

  const seen = useMutation({
    mutationFn: () => apiClient.post('/suggestions/seen'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suggestions', 'unseen-count'] }),
  });
  useEffect(() => {
    if (list.isSuccess && !markedSeen.current) {
      markedSeen.current = true;
      seen.mutate();
    }
  }, [list.isSuccess, seen]);

  const rows = list.data ?? [];
  const visible = rows.filter((s) => s.response?.type !== 'DISMISSED');
  const hidden = rows.filter((s) => s.response?.type === 'DISMISSED');
  const openId = params.get('id');
  const open = openId ? (rows.find((s) => s.id === openId) ?? null) : null;

  function setOpen(id: string | null) {
    setParams(id ? { id } : {}, { replace: true });
  }

  const card = (s: ClientSuggestion) => (
    <SuggestionCard
      key={s.id}
      suggestion={s}
      onOpen={() => setOpen(s.id)}
      muted={s.response?.type === 'DISMISSED'}
      badges={
        s.isNew && !s.response ? (
          <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-white">
            Nouveau
          </span>
        ) : null
      }
      footer={
        s.response?.type === 'INTERESTED' ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
            Demande envoyée
          </span>
        ) : s.response?.type === 'DISMISSED' ? (
          <span className="text-xs text-slate-500">Masquée</span>
        ) : (
          <span className="text-xs font-medium text-brand">Découvrir →</span>
        )
      }
    />
  );

  return (
    <>
      <SuggestionDetailModal suggestion={open} onClose={() => setOpen(null)} />
      <section className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {list.isLoading && <p className="text-sm text-slate-500">Chargement…</p>}
        {list.isError && (
          <p role="alert" className="text-sm text-red-600">
            Les suggestions n’ont pas pu être chargées.
          </p>
        )}
        {list.data && visible.length === 0 && (
          <EmptyShowcase text="Aucune suggestion pour le moment. Adweb-Systèmes vous proposera ici de nouveaux services, testés et prêts à vous faire gagner du temps." />
        )}
        {visible.length > 0 && <div className={GRID}>{visible.map(card)}</div>}
        {hidden.length > 0 && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setShowHidden((v) => !v)}
              aria-expanded={showHidden}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
            >
              <ChevronDown
                className={clsx('h-3.5 w-3.5 transition', showHidden && 'rotate-180')}
                aria-hidden="true"
              />
              Suggestions masquées ({hidden.length})
            </button>
            {showHidden && <div className={GRID}>{hidden.map(card)}</div>}
          </div>
        )}
      </section>
    </>
  );
}

const STATUS_FILTERS: Array<{ value: SuggestionStatus | ''; label: string }> = [
  { value: '', label: 'Toutes' },
  { value: 'DRAFT', label: 'Brouillons' },
  { value: 'PUBLISHED', label: 'Publiées' },
  { value: 'ARCHIVED', label: 'Archivées' },
];

/** Vue Adweb : toutes les suggestions, leur diffusion et l'intérêt des clients. */
function AdminSuggestions({
  editorOpen,
  setEditorOpen,
}: {
  editorOpen: boolean;
  setEditorOpen: (open: boolean) => void;
}) {
  const [params, setParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<SuggestionStatus | ''>('');
  const [editing, setEditing] = useState<AdminSuggestion | null>(null);

  const list = useQuery({
    queryKey: ['suggestions', 'list'],
    queryFn: async () => (await apiClient.get<AdminSuggestion[]>('/suggestions')).data,
  });
  const rows = list.data ?? [];
  const filtered = statusFilter ? rows.filter((s) => s.status === statusFilter) : rows;
  const categories = [...new Set(rows.map((s) => s.category).filter((c): c is string => !!c))];
  const openId = params.get('id');

  function setOpen(id: string | null) {
    setParams(id ? { id } : {}, { replace: true });
  }

  return (
    <>
      <SuggestionAdminModal
        suggestionId={editorOpen ? null : openId}
        onClose={() => setOpen(null)}
        onEdit={(s) => {
          setEditing(s);
          setEditorOpen(true);
        }}
      />
      <SuggestionEditorModal
        open={editorOpen}
        suggestion={editing}
        categories={categories}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
        onSaved={(s) => {
          setEditorOpen(false);
          setEditing(null);
          setOpen(s.id);
        }}
      />
      <section className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-4 flex flex-wrap items-center gap-2" role="group" aria-label="Filtrer">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              aria-pressed={statusFilter === f.value}
              className={clsx(
                'rounded-full px-3 py-1.5 text-xs font-medium transition',
                statusFilter === f.value
                  ? 'bg-brand text-white'
                  : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50',
              )}
            >
              {f.label}
              {f.value && ` (${rows.filter((s) => s.status === f.value).length})`}
            </button>
          ))}
        </div>
        {list.isLoading && <p className="text-sm text-slate-500">Chargement…</p>}
        {list.isError && (
          <p role="alert" className="text-sm text-red-600">
            Les suggestions n’ont pas pu être chargées.
          </p>
        )}
        {list.data && filtered.length === 0 ? (
          <EmptyShowcase
            text={
              statusFilter
                ? 'Aucune suggestion dans cet état.'
                : 'Aucune suggestion pour le moment. Proposez à vos clients un service que vous avez testé : ils pourront demander à en profiter en un clic.'
            }
            action={
              !statusFilter && (
                <Button size="sm" onClick={() => setEditorOpen(true)}>
                  Nouvelle suggestion
                </Button>
              )
            }
          />
        ) : (
          <div className={GRID}>
            {filtered.map((s) => (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                onOpen={() => setOpen(s.id)}
                muted={s.status === 'ARCHIVED'}
                badges={<SuggestionStatusPill status={s.status} />}
                footer={
                  <span className="flex flex-col items-end text-right text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1 font-medium text-slate-700">
                      <ThumbsUp className="h-3 w-3" aria-hidden="true" />
                      {s.interestedCount} intéressé{s.interestedCount > 1 ? 's' : ''}
                    </span>
                    <span>{targetLabel(s)}</span>
                  </span>
                }
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

/**
 * Suggestions : Adweb-Systèmes y propose de nouveaux services ; les clients
 * demandent à en profiter en un clic (« Ça m'intéresse » crée une demande).
 */
export function SuggestionsPage() {
  const { user } = useAuth();
  const isPlatform = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';
  const [editorOpen, setEditorOpen] = useState(false);

  return (
    <>
      <PageHeader
        title="Suggestions"
        subtitle={
          isPlatform
            ? 'Les services que vous proposez à vos clients, et leur intérêt.'
            : 'Des automatisations testées par Adweb-Systèmes, prêtes à vous faire gagner du temps.'
        }
        helpTopic={isPlatform ? 'suggestions-adweb' : 'suggestions'}
        actions={
          isPlatform ? (
            <Button variant="inverse" size="sm" onClick={() => setEditorOpen(true)}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Nouvelle suggestion
            </Button>
          ) : undefined
        }
      />
      {isPlatform ? (
        <AdminSuggestions editorOpen={editorOpen} setEditorOpen={setEditorOpen} />
      ) : (
        <ClientSuggestions />
      )}
    </>
  );
}
